import { Fragment, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon  } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * items: array d'objets
 * selectedItems: array d'objets sélectionnés
 * onChange: (newSelectedItems) => void
 * itemToString: (item) => string
 * includeAllOption: si true, gère l'item {name:'all'} comme "tout"
 */
export const InputMultiSelectCheckbox = ({
  className = "",
  items = [],
  selectedItems = [],
  onChange,
  itemToString,
  disabled = false,
  category = "primary",
  includeAllOption = true
}) => {
  const allItem = useMemo(() => items.find((i) => i?.name === "all"), [items]);
  const selectedNames = useMemo(
    () => new Set((selectedItems ?? []).map((i) => i?.name)),
    [selectedItems]
  );

  const isAllSelected = includeAllOption && allItem && selectedNames.has("all");

  const label = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return "SÉLECTIONNER…";
    if (isAllSelected) return itemToString(allItem);
    return selectedItems.map(itemToString).join(", ");
  }, [selectedItems, isAllSelected, allItem, itemToString]);

  const containerStyle =
    category === "primary"
      ? "bg-perception-gray-800 text-white"
      : "bg-transparent border border-perception-gray-500 text-perception-gray-800";

  const onToggle = (item) => {
    if (disabled) return;

    // Cas "TOUS"
    if (includeAllOption && item?.name === "all") {
      if (isAllSelected) {
        onChange?.([]); // décocher "TOUS" => rien
      } else {
        onChange?.([allItem]); // cocher "TOUS" => uniquement "TOUS"
      }
      return;
    }

    // Si "TOUS" était sélectionné, on l'enlève dès qu'on choisit un type spécifique
    let base = (selectedItems ?? []).filter((i) => i?.name !== "all");

    const exists = base.some((i) => i?.id === item?.id);
    const next = exists ? base.filter((i) => i?.id !== item?.id) : [...base, item];

    onChange?.(next);
  };

  return (
    <Popover className={classNames("relative w-[320px]", className)}>
      <Popover.Button
        disabled={disabled}
        className={classNames(
          "w-full flex items-center justify-between px-3 py-1.5 text-xs uppercase rounded-md focus:outline-none",
          containerStyle,
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        <span className="truncate text-left">{label}</span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Popover.Panel className="absolute z-20 mt-2 w-full rounded-md bg-perception-gray-800 shadow-lg ring-1 ring-black/10">
          <div className="max-h-72 overflow-auto p-2 text-xs">
            {items.map((item) => {
              const checked = isAllSelected
                ? item?.name === "all"
                : selectedNames.has(item?.name);

              // si "TOUS" coché, on désactive les autres cases (optionnel mais clair)
              const isDisabledBecauseAll = isAllSelected && item?.name !== "all";

              return (
                <label
                  key={item.id}
                  className={classNames(
                    "flex items-center gap-2 px-2 py-2 rounded cursor-pointer select-none",
                    "hover:bg-perception-black-800",
                    isDisabledBecauseAll ? "opacity-50 cursor-not-allowed" : ""
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checked}
                    disabled={disabled || isDisabledBecauseAll}
                    onChange={() => onToggle(item)}
                  />
                  <span className="truncate text-white">{itemToString(item)}</span>
                </label>
              );
            })}
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};
